/** @file One CommandForm field: the ui-kit control for its kind, a null toggle, nested groups, its error. */
import type { ReactNode } from 'react';
import { ENTITY_KINDS } from '@pose2d/format/doc/entityKinds';
import type { Doc } from '@pose2d/format/types';
import type { Translate } from '@pose2d/i18n/types';
import { Checkbox } from '../ui-kit/Checkbox';
import { ColorField } from '../ui-kit/ColorField';
import { NumberField } from '../ui-kit/NumberField';
import { Select } from '../ui-kit/Select';
import { TextInput } from '../ui-kit/TextInput';
import { JsonField } from './JsonField';
import { neutralValue } from './neutralValue';
import type { FieldSpec } from './schemaFields';

/** What every field control needs; `error.at` is the path key the last error points at. */
export interface FieldProps {
  field: FieldSpec;
  values: ReadonlyMap<string, unknown>;
  onEdit: (path: string, value: unknown) => void;
  doc: Doc;
  t: Translate;
  error: { at: string; text: string } | null;
}

type Control<K extends FieldSpec['kind']> = (field: Extract<FieldSpec, { kind: K }>, value: unknown, set: (v: unknown) => void, p: FieldProps) => ReactNode;

const NONE = { value: '', label: '—' };
const labelled = (label: string, control: ReactNode): ReactNode => <label className="p2-form-row"><span className="p2-form-label">{label}</span>{control}</label>;
const num = (v: unknown): number => (typeof v === 'number' ? v : 0);
const vec = (v: unknown, size: number): number[] => Array.from({ length: size }, (_, i) => num(Array.isArray(v) ? v[i] : undefined));
const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const bounds = (f: Extract<FieldSpec, { kind: 'number' }>) => ({ ...(f.min === null ? {} : { min: f.min }), ...(f.max === null ? {} : { max: f.max }), ...(f.integer ? { precision: 0 } : {}) });
const names = (doc: Doc, f: Extract<FieldSpec, { kind: 'ref' }>) =>
  [NONE, ...Object.values(doc[ENTITY_KINDS[f.entity]] as Record<string, { name: string }>).map((e) => e.name).sort().map((n) => ({ value: n, label: n }))];

const CONTROLS: { [K in FieldSpec['kind']]: Control<K> } = {
  number: (f, v, set) => <NumberField label={f.key} value={typeof v === 'number' ? v : 0} {...bounds(f)} step={f.integer ? 1 : 0.1} onCommit={set} />,
  text: (f, v, set) => labelled(f.key, <TextInput label={f.key} value={text(v)} onCommit={set} />),
  flag: (f, v, set) => <Checkbox label={f.key} checked={v === true} onChange={set} />,
  choice: (f, v, set) => labelled(f.key, <Select label={f.key} value={String(v)} options={f.options.map((o) => ({ value: String(o), label: String(o) }))} onChange={(s) => { set(f.options.find((o) => String(o) === s) ?? s); }} />),
  color: (f, v, set, p) => labelled(f.key, <ColorField value={text(v)} alpha={f.alpha} labels={{ hex: f.key, pick: p.t('forms.color.pick'), alpha: p.t('forms.color.alpha') }} onCommit={set} />),
  vector: (f, v, set) => labelled(f.key, <span className="p2-form-vector">{vec(v, f.size).map((n, i) => <NumberField key={i} label={`${f.key}[${String(i)}]`} value={n} step={0.1} onCommit={(next) => { set(vec(v, f.size).map((old, j) => (j === i ? next : old))); }} />)}</span>),
  ref: (f, v, set, p) => labelled(f.key, <Select label={f.key} value={text(v)} options={names(p.doc, f)} onChange={set} />),
  group: (f, _v, _set, p) => <fieldset className="p2-form-group"><legend>{f.key}</legend>{f.fields.map((c) => <FieldInput key={c.key} {...p} field={c} />)}</fieldset>,
  json: (f, v, set) => <JsonField label={f.key} value={text(v)} onCommit={set} />,
};

/** Null toggle (nullable fields), the control while a value is present, and the error line of this field. */
export function FieldInput(p: FieldProps) {
  const { field, values, onEdit, t, error } = p;
  const key = field.path.join('.');
  const value = values.get(key);
  const set = (v: unknown): void => { onEdit(key, v); };
  const present = !field.nullable || value !== null;
  const control = (CONTROLS[field.kind] as Control<FieldSpec['kind']>)(field, value, set, p);
  return (
    <div className="p2-form-field">
      {field.nullable && <Checkbox label={t('forms.set', { field: field.key })} checked={present} onChange={(on) => { set(on ? neutralValue(field) : null); }} />}
      {present && control}
      {error?.at === key && <p className="p2-form-error" role="alert">{error.text}</p>}
    </div>
  );
}
